import React, { useMemo, useState } from 'react'
import { Col, Badge, Modal, Button, Row, Table, Form, InputGroup } from 'react-bootstrap'
import { FaTimes } from 'react-icons/fa'
import { DisallowedDomainRule, DisallowedRuleMode } from './script-settings-model'

interface Props {
    allowedDomains: string[]
    disallowedDomainRules: DisallowedDomainRule[]
    onChange(domains: string[], disallowedDomainRules: DisallowedDomainRule[]): void
}

const RULE_MODE_OPTIONS: Array<{ value: DisallowedRuleMode; label: string }> = [
    { value: 'all', label: 'Block both (all)' },
    { value: 'pageviews', label: 'Block page views only' },
    { value: 'clicks', label: 'Block clicks only' },
]

function formatRuleCountLabel(count: number) {
    return count === 1 ? '1 rule' : `${count} rules`
}

const DomainSettings: React.FC<Props> = ({ allowedDomains, disallowedDomainRules, onChange }) => {
    const [showError, setShowError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [showAddDomainModal, setShowAddDomainModal] = useState(false)
    const [domainInputError, setDomainInputError] = useState('')
    const [selectedDomainForRules, setSelectedDomainForRules] = useState<string | null>(null)
    const [newDomainDraft, setNewDomainDraft] = useState('')
    const [ruleDraft, setRuleDraft] = useState('')
    const [ruleDraftMode, setRuleDraftMode] = useState<DisallowedRuleMode>('all')

    const stripCommonSubdomain = (hostname: string) => hostname.replace(/^www\d*\./i, '')

    const cleanDomain = (input: string) => {
        try {
            const url = input.startsWith('http') ? input : `https://${input}`
            const parsed = new URL(url)
            return stripCommonSubdomain(parsed.hostname.toLowerCase())
        } catch {
            return null
        }
    }

    const isValidDomain = (domain: string) => {
        return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
    }

    const normalizePathPattern = (raw: string) => {
        let value = raw.trim()
        if (!value) return null

        value = value.split(/[?#]/)[0] || '/'
        if (!value.startsWith('/')) value = `/${value}`
        value = value.replace(/\/+/g, '/')

        if (value !== '/' && value.endsWith('/') && !value.endsWith('/*')) {
            value = value.slice(0, -1)
        }

        if (value.endsWith('/*')) {
            const prefix = value.slice(0, -2).replace(/\/+$/, '')
            value = `${prefix || ''}/*`
            if (!value.startsWith('/')) value = `/${value}`
        }

        if (!value.startsWith('/')) return null
        if (value.includes(' ')) return null

        return value
    }

    const rulesByDomain = useMemo(() => {
        const grouped: Record<string, DisallowedDomainRule[]> = {}

        for (const domain of allowedDomains) {
            grouped[domain] = []
        }

        for (const rule of disallowedDomainRules) {
            if (!grouped[rule.domain]) continue
            grouped[rule.domain].push(rule)
        }

        return grouped
    }, [allowedDomains, disallowedDomainRules])

    const addDomain = (rawValue: string) => {
        const value = rawValue.trim().toLowerCase()
        if (!value) {
            setDomainInputError('Please enter a domain.')
            return false
        }

        const domain = cleanDomain(value)
        if (!domain || !isValidDomain(domain)) {
            setDomainInputError(`"${value}" is not a valid domain.`)
            return false
        }

        if (allowedDomains.includes(domain)) {
            setNewDomainDraft('')
            setDomainInputError('')
            return true
        }

        onChange([...allowedDomains, domain], disallowedDomainRules)
        setNewDomainDraft('')
        setDomainInputError('')
        return true
    }

    const addRuleToDomain = (domain: string, rawRule: string, mode: DisallowedRuleMode) => {
        const normalizedPath = normalizePathPattern(rawRule)

        if (!normalizedPath) {
            setErrorMessage(`"${rawRule}" is not a valid path. Example: / or /checkout/*`)
            setShowError(true)
            return
        }

        const existing = disallowedDomainRules.find((rule) => rule.domain === domain && rule.path === normalizedPath)

        if (existing) {
            const updatedRules = disallowedDomainRules.map((rule) => {
                if (rule.domain === domain && rule.path === normalizedPath) {
                    return { ...rule, mode }
                }
                return rule
            })
            onChange(allowedDomains, updatedRules)
            return
        }

        onChange(allowedDomains, [...disallowedDomainRules, { domain, path: normalizedPath, mode }])
    }

    const removeDomain = (domain: string) => {
        onChange(
            allowedDomains.filter(d => d !== domain),
            disallowedDomainRules.filter(rule => rule.domain !== domain)
        )
    }

    const removeDisallowedRule = (domain: string, path: string) => {
        onChange(
            allowedDomains,
            disallowedDomainRules.filter((rule) => !(rule.domain === domain && rule.path === path))
        )
    }

    const updateRuleMode = (domain: string, path: string, mode: DisallowedRuleMode) => {
        onChange(
            allowedDomains,
            disallowedDomainRules.map((rule) => {
                if (rule.domain === domain && rule.path === path) {
                    return { ...rule, mode }
                }
                return rule
            })
        )
    }

    return (
        <Col xs={12} className="mb-0">
            <Row className="g-3">
                <Col xs={12} md={6} className="mb-1 mb-md-2">
                    <h5>Allowed Domains</h5>
                    <p className="text-muted mb-2">
                        Whitelist domains where the script is allowed to run.
                    </p>

                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            setDomainInputError('')
                            setShowAddDomainModal(true)
                        }}
                    >
                        Add domain
                    </Button>
                </Col>

                <Col xs={12} className="mt-2">
                    <div className="border rounded-3 overflow-hidden">
                        <Table responsive hover size="sm" className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '50%' }} className="text-uppercase small text-muted">Domain</th>
                                    <th style={{ width: '16%' }} className="text-uppercase small text-muted">Rules</th>
                                    <th style={{ width: '34%' }} className="text-uppercase small text-muted">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allowedDomains.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="text-muted py-3 small">
                                            No domains yet. Add your first allowed domain above.
                                        </td>
                                    </tr>
                                )}

                                {allowedDomains.map((domain) => {
                                    const domainRules = rulesByDomain[domain] || []

                                    return (
                                        <tr key={domain}>
                                            <td className="py-2">
                                                <span className="fw-semibold">{domain}</span>
                                            </td>
                                            <td className="py-2">
                                                {domainRules.length > 0 ? (
                                                    <Badge bg="secondary" className="fw-normal">{formatRuleCountLabel(domainRules.length)}</Badge>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>
                                            <td className="py-2">
                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => setSelectedDomainForRules(domain)}
                                                    >
                                                        Manage rules
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => removeDomain(domain)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>

            <Modal
                show={!!selectedDomainForRules}
                onHide={() => {
                    setSelectedDomainForRules(null)
                    setRuleDraft('')
                    setRuleDraftMode('all')
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Manage rules for <strong>{selectedDomainForRules}</strong></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label small text-muted">Add blocked path rule</label>
                        <InputGroup size="sm" className="mb-2">
                            <InputGroup.Text>{selectedDomainForRules}</InputGroup.Text>
                            <Form.Control
                                placeholder="/ or /path/*"
                                value={ruleDraft}
                                onChange={(event) => setRuleDraft(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && selectedDomainForRules) {
                                        event.preventDefault()
                                        addRuleToDomain(selectedDomainForRules, ruleDraft, ruleDraftMode)
                                        setRuleDraft('')
                                    }
                                }}
                            />
                            <Form.Select
                                style={{ maxWidth: '220px' }}
                                value={ruleDraftMode}
                                onChange={(event) => setRuleDraftMode(event.target.value as DisallowedRuleMode)}
                            >
                                {RULE_MODE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    if (selectedDomainForRules) {
                                        addRuleToDomain(selectedDomainForRules, ruleDraft, ruleDraftMode)
                                        setRuleDraft('')
                                    }
                                }}
                            >
                                Add rule
                            </Button>
                        </InputGroup>
                    </div>

                    {selectedDomainForRules && rulesByDomain[selectedDomainForRules]?.length > 0 ? (
                        <div>
                            <label className="form-label text-muted small d-block mb-2">Active rules</label>
                            <div className="d-flex flex-column gap-2">
                                {rulesByDomain[selectedDomainForRules].map((rule) => {
                                    const fullRule = `${selectedDomainForRules}${rule.path}`

                                    return (
                                        <div key={fullRule} className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="small fw-semibold">{rule.path}</span>
                                                <span className="small text-muted">-</span>
                                                <span className="small text-muted">
                                                    {RULE_MODE_OPTIONS.find((option) => option.value === rule.mode)?.label || 'Block both (all)'}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Select
                                                    size="sm"
                                                    style={{ maxWidth: '220px' }}
                                                    value={rule.mode}
                                                    onChange={(event) => updateRuleMode(rule.domain, rule.path, event.target.value as DisallowedRuleMode)}
                                                >
                                                    {RULE_MODE_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeDisallowedRule(rule.domain, rule.path)}
                                                >
                                                    <FaTimes />
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-muted small">No rules yet. Add your first rule above.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedDomainForRules(null)}>
                        Done
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showAddDomainModal}
                onHide={() => {
                    setShowAddDomainModal(false)
                    setNewDomainDraft('')
                    setDomainInputError('')
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add domain</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label className="form-label small text-muted">Domain</label>
                    <InputGroup size="sm">
                        <Form.Control
                            placeholder="example.com"
                            value={newDomainDraft}
                            onChange={(event) => {
                                setNewDomainDraft(event.target.value)
                                if (domainInputError) setDomainInputError('')
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    if (addDomain(newDomainDraft)) {
                                        setShowAddDomainModal(false)
                                    }
                                }
                            }}
                            autoFocus
                        />
                    </InputGroup>
                    {domainInputError && (
                        <div className="small text-danger mt-2">{domainInputError}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                            setShowAddDomainModal(false)
                            setNewDomainDraft('')
                            setDomainInputError('')
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            if (addDomain(newDomainDraft)) {
                                setShowAddDomainModal(false)
                            }
                        }}
                    >
                        Add
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showError} onHide={() => setShowError(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Invalid Value</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorMessage}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowError(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Col>
    )
}

export default DomainSettings
